import React from 'react';
import { Box, Divider, Group } from '@mantine/core';
import { useForm } from '@mantine/hooks';
import { useNotifications } from '@mantine/notifications';
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

import { useIsMobileMedia, useNextPath, useRequestStates } from '~/hooks';
import { authContextSlice, useAuthContext } from '~/pages/LandingPage/contexts/AuthContext';
import { useLoginMutation } from '~/redux/slices/userSlice';
import {
  LabelMedium,
  LabelMediumStrong,
  LabelSmall,
  LabelSmallStrong,
  ParagraphSmall,
} from '~/pages/LandingPage/typography';
import { getFirebaseAuth } from '~/lib/firebase';
import { LoaderWithOverlay } from '~/components/molecules';

import GoogleIcon from '~/assets/companyLogos/google.svg';

import * as Styles from '../AuthModal.styled';

const NOTIFICATION_TIMEOUT = 5000;

const LoginView = () => {
  const isMobileMedia = useIsMobileMedia();
  const nextPath = useNextPath('/candidates/browse/');
  const navigate = useNavigate();
  const { showNotification } = useNotifications();

  const { dispatchAuthCtx } = useAuthContext();

  const [loginUser] = useLoginMutation();
  const [loginReqStates, loginReqStateHandler] = useRequestStates();

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    errorMessages: {
      email: 'This field can not be blank',
      password: 'This field can not be blank',
    },
    validationRules: {
      email: (v) => v.trim() !== '',
      password: (v) => v.trim() !== '',
    },
  });

  const handleRegisterClick = () => {
    dispatchAuthCtx(authContextSlice.actions.setView('signup'));
  };

  const handleForgotPasswordClick = () => {
    dispatchAuthCtx(authContextSlice.actions.setView('forgot-password'));
  };

  const handleSubmit = async ({ email, password }) => {
    try {
      loginReqStateHandler.setPending();
      const auth = getFirebaseAuth();
      const firebaseUserCreds = await signInWithEmailAndPassword(auth, email, password);
      const upraisedLoginErrorMsg = await loginUpraisedAccount(firebaseUserCreds);
      if (upraisedLoginErrorMsg) {
        loginReqStateHandler.setRejected(upraisedLoginErrorMsg);
      }
      loginReqStateHandler.setFulfilled(firebaseUserCreds);
    } catch (e) {
      let message = 'Invalid email or password';
      showNotification({ title: 'Error', message, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
      loginReqStateHandler.setRejected(message);
    }
  };

  const handleLoginWithGoogle = async () => {
    try {
      loginReqStateHandler.setPending();
      const auth = getFirebaseAuth();
      const google = new GoogleAuthProvider();
      const firebaseUserCreds = await signInWithPopup(auth, google);
      const upraisedLoginErrorMsg = await loginUpraisedAccount(firebaseUserCreds);
      if (upraisedLoginErrorMsg) {
        loginReqStateHandler.setRejected(upraisedLoginErrorMsg);
      } else {
        loginReqStateHandler.setFulfilled(firebaseUserCreds);
      }
    } catch (e) {
      let message = `Something went wrong. Code: ${e.code}`;
      if (e.code === 'auth/popup-closed-by-user') {
        message = 'Looks like you closed the authentication popup. Please try again.';
      } else if (e.code === 'auth/user-disabled') {
        message = 'Your account is disabled. Please contact support.';
      }
      showNotification({ title: 'Error', message, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
      loginReqStateHandler.setRejected(message);
    }
  };

  const loginUpraisedAccount = async (firebaseUserCreds) => {
    const resp = await loginUser({ idToken: firebaseUserCreds.user.accessToken });
    let message = '';
    if (resp.error) {
      const data = resp.error.data;
      const errorCode = data?.errorCode;
      if (errorCode === 'INVALID_FIREBASE_TOKEN') {
        message = 'Authentication failed. Please try again.';
      } else if (errorCode === 'USER_DOES_NOT_EXIST') {
        message = 'User not found. Please register';
      } else {
        message = data?.error || 'Something went wrong';
      }
    } else {
      const respUser = resp.data?.user;
      if (respUser && !respUser.isEmailVerified) {
        dispatchAuthCtx(authContextSlice.actions.setView('verify-email'));
      } else {
        navigate(nextPath);
        return;
      }
    }

    if (message) {
      showNotification({ title: 'Error', message, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
    }
    return message;
  };

  return (
    <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
      <LoaderWithOverlay visible={loginReqStates.isPending} />
      <Styles.ContinueWithGoogleBtn
        mt={isMobileMedia ? '24px' : '32px'}
        fullWidth
        leftIcon={<GoogleIcon width="24px" />}
        onClick={handleLoginWithGoogle}
      >
        <LabelMediumStrong>Continue with Google</LabelMediumStrong>
      </Styles.ContinueWithGoogleBtn>
      <Divider
        my={isMobileMedia ? '16px' : '24px'}
        label={<LabelSmall color="#818181">OR</LabelSmall>}
        labelPosition="center"
      />
      <Box mt={isMobileMedia ? '16px' : '24px'}>
        <Styles.FormTextInput
          label={<LabelMedium>Work Email</LabelMedium>}
          type="email"
          placeholder="E.g. rohan@company.com"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          autoComplete="on"
          {...form.getInputProps('email')}
        />
      </Box>
      <Box mt={isMobileMedia ? '16px' : '24px'}>
        <Styles.FormPasswordInput
          label={<LabelMedium>Password</LabelMedium>}
          type="password"
          {...form.getInputProps('password')}
        />
        <LabelSmallStrong
          mt={isMobileMedia ? '4px' : '8px'}
          color="#434343"
          align="right"
          onClick={handleForgotPasswordClick}
          style={{ cursor: 'pointer' }}
        >
          Forgot Password?
        </LabelSmallStrong>
      </Box>
      <Box mt={isMobileMedia ? '24px' : '32px'}>
        <Styles.PrimaryBtn fullWidth type="submit">
          <LabelMediumStrong color="#fff">Log In</LabelMediumStrong>
        </Styles.PrimaryBtn>
      </Box>
      <Group mt="12px" spacing="4px">
        <ParagraphSmall>Don’t have an account?</ParagraphSmall>
        <LabelSmallStrong color="#1F7AE0" onClick={handleRegisterClick} style={{ cursor: 'pointer' }}>
          Register
        </LabelSmallStrong>
      </Group>
    </Box>
  );
};

export default LoginView;
